import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * Brand identity lockup: a generated geometric monogram plus real wordmark typography.
 *
 * A stock icon beside plain text reads as an unstyled placeholder, and it is the first thing seen on
 * every page. The mark below is DERIVED from the brand's own initials and drawn as SVG, so any brand
 * has a credible identity with no asset, at any size, in both themes and over photography. A real
 * logo image is still preferred when one exists: pass `logo` and it replaces the generated mark.
 */

export type BrandLockupTone = 'brand' | 'mono' | 'contrast'
export type BrandLockupSize = 'sm' | 'md' | 'lg'
/** Silhouette of the generated mark. Varying it is what stops a row of lockups reading as one chip. */
export type BrandMarkShape = 'chamfer' | 'circle' | 'hex' | 'square'

export const BRAND_MARK_SHAPES: readonly BrandMarkShape[] = [
    'chamfer',
    'circle',
    'hex',
    'square',
] as const

/** Stable per-name index into a list; the same brand always draws the same mark. */
export function brandVariantIndex(name: string, length: number): number {
    let hash = 0
    for (let i = 0; i < name.length; i += 1) {
        hash = (hash * 31 + name.charCodeAt(i)) % 100003
    }
    return hash % length
}

export interface BrandLockupProps {
    /** Brand name. Sets the wordmark and seeds the monogram. */
    name: string
    /**
     * Secondary word set beneath the wordmark, smaller and letterspaced — the "Salt. ARCHITECTS"
     * treatment. Use a category word ('architects', 'construction'), never a tagline.
     */
    secondary?: string
    /** Real logo image URL. Replaces the generated monogram when supplied. */
    logo?: string
    /** Alt text for the logo image; defaults to "<name> logo". */
    logoAlt?: string
    /**
     * `brand` fills the mark with the primary colour. `mono` is the solid single-ink lockup a logo
     * strip needs. `contrast` is hollow line-art in `currentColor`, for placement over photography
     * inside a `photo-scrim__mark` wrapper.
     */
    tone?: BrandLockupTone
    size?: BrandLockupSize
    /** Silhouette of the generated mark; derived from the name when omitted. */
    shape?: BrandMarkShape
    /** Renders the mark on its own, without the wordmark. */
    markOnly?: boolean
    /** Extra classes on the wordmark itself, for a strip that needs varied logo typography. */
    wordmarkClassName?: string
    className?: string
}

/** Words that carry no identity and are skipped when picking initials. */
const FILLER_WORDS = new Set(['and', 'the', 'of', 'for', 'a', 'an'])

/** "Granite & Grain" -> "GG", "Acme" -> "A". Exported so callers can label the mark consistently. */
export function brandInitials(name: string): string {
    const words = name
        .split(/[\s\-_/]+/)
        .map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
        .filter((word) => word.length > 0 && !FILLER_WORDS.has(word.toLowerCase()))

    if (words.length === 0) return name.trim().slice(0, 1).toUpperCase() || 'A'
    if (words.length === 1) return words[0].slice(0, 1).toUpperCase()
    return (words[0].slice(0, 1) + words[1].slice(0, 1)).toUpperCase()
}

const SIZES = {
    sm: {
        mark: 'size-7',
        image: 'h-6',
        gap: 'gap-2',
        name: 'text-sm font-semibold tracking-[-0.01em]',
        secondary: 'text-[0.5rem] font-medium uppercase tracking-[0.28em]',
        stack: 'gap-[0.15rem]',
    },
    md: {
        mark: 'size-9',
        image: 'h-8',
        gap: 'gap-2.5',
        name: 'text-[1.0625rem] font-semibold tracking-[-0.02em]',
        secondary: 'text-[0.5625rem] font-medium uppercase tracking-[0.3em]',
        stack: 'gap-[0.2rem]',
    },
    lg: {
        mark: 'size-11',
        image: 'h-10',
        gap: 'gap-3',
        name: 'text-2xl font-semibold tracking-[-0.025em]',
        secondary: 'text-[0.625rem] font-medium uppercase tracking-[0.34em]',
        stack: 'gap-[0.25rem]',
    },
} as const

/**
 * Chamfered squircle: a rounded square with its top-right corner cut on the diagonal. The chamfer is
 * what stops the mark reading as "letters in a box" — it is a deliberate silhouette that survives
 * being scaled down to a favicon.
 */
const SHAPE_PATHS: Record<BrandMarkShape, string> = {
    chamfer:
        'M12.5 1H24.8L39 15.2V27.5A11.5 11.5 0 0 1 27.5 39H12.5A11.5 11.5 0 0 1 1 27.5V12.5A11.5 11.5 0 0 1 12.5 1Z',
    circle: 'M39 20A19 19 0 1 1 1 20A19 19 0 1 1 39 20Z',
    hex: 'M10.5 3.5H29.5L39 20L29.5 36.5H10.5L1 20Z',
    square: 'M6 1H34A5 5 0 0 1 39 6V34A5 5 0 0 1 34 39H6A5 5 0 0 1 1 34V6A5 5 0 0 1 6 1Z',
}

/** The fold the chamfer implies, painted a shade off the tile so the mark has one plane of depth. */
const FOLD_PATH = 'M24.8 1L39 15.2H26.8A2 2 0 0 1 24.8 13.2Z'

/** Type-scale inside the 40-unit mark: two letters have to give ground to fit the silhouette. */
const markTypeScale = (initials: string) => ({
    fontSize: initials.length > 1 ? 15 : 19,
    letterSpacing: initials.length > 1 ? -0.8 : 0,
})

function BrandMark({
    initials,
    tone,
    shape,
    className,
}: {
    initials: string
    tone: BrandLockupTone
    shape: BrandMarkShape
    className?: string
}) {
    // React's useId contains ':' characters, which are not valid inside a url(#…) reference.
    const markId = `brand-mark-${React.useId().replace(/:/g, '')}`
    const type = markTypeScale(initials)
    const path = SHAPE_PATHS[shape]

    // A solid mark with the letterform cut out of it, the way a real logo is drawn. `contrast` keeps
    // the tile hollow instead: over photography a knockout would fill the counters with the photo.
    const knockout = tone === 'mono'
    const hollow = tone === 'contrast'

    return (
        <svg
            viewBox="0 0 40 40"
            role="presentation"
            aria-hidden
            className={cn('shrink-0', className)}>
            <defs>
                {tone === 'brand' && (
                    <linearGradient id={`${markId}-fill`} x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" style={{ stopColor: 'hsl(var(--primary))' }} />
                        <stop offset="100%" style={{ stopColor: 'hsl(var(--primary) / 0.78)' }} />
                    </linearGradient>
                )}
                {knockout && (
                    <mask id={`${markId}-mask`}>
                        <path d={path} fill="white" />
                        <text
                            x="20"
                            y="21"
                            textAnchor="middle"
                            dominantBaseline="central"
                            fontFamily="inherit"
                            fontWeight={700}
                            fill="black"
                            {...type}>
                            {initials}
                        </text>
                    </mask>
                )}
            </defs>

            <path
                d={path}
                mask={knockout ? `url(#${markId}-mask)` : undefined}
                style={
                    tone === 'brand'
                        ? { fill: `url(#${markId}-fill)` }
                        : hollow
                          ? {
                                fill: 'none',
                                stroke: 'currentColor',
                                strokeWidth: 1.6,
                            }
                          : { fill: 'currentColor' }
                }
            />

            {tone === 'brand' && shape === 'chamfer' && (
                <path d={FOLD_PATH} style={{ fill: 'hsl(var(--primary-foreground) / 0.22)' }} />
            )}

            {!knockout && (
                <text
                    x="20"
                    y="21"
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontFamily="inherit"
                    fontWeight={700}
                    style={{
                        fill:
                            tone === 'brand'
                                ? 'hsl(var(--primary-foreground))'
                                : 'currentColor',
                    }}
                    {...type}>
                    {initials}
                </text>
            )}
        </svg>
    )
}

export function BrandLockup({
    name,
    secondary,
    logo,
    logoAlt,
    tone = 'brand',
    size = 'md',
    shape,
    markOnly = false,
    wordmarkClassName,
    className,
}: BrandLockupProps) {
    const scale = SIZES[size]
    const initials = React.useMemo(() => brandInitials(name), [name])
    const markShape =
        shape ?? BRAND_MARK_SHAPES[brandVariantIndex(name, BRAND_MARK_SHAPES.length)]

    return (
        <span className={cn('inline-flex items-center', scale.gap, className)}>
            {logo ? (
                <img
                    src={logo}
                    alt={logoAlt ?? `${name} logo`}
                    loading="lazy"
                    className={cn('w-auto max-w-40 object-contain', scale.image)}
                />
            ) : (
                <BrandMark
                    initials={initials}
                    tone={tone}
                    shape={markShape}
                    className={scale.mark}
                />
            )}
            {!markOnly && (
                <span className={cn('flex flex-col leading-none', scale.stack)}>
                    <span className={cn(scale.name, wordmarkClassName)}>{name}</span>
                    {secondary && (
                        <span className={cn(scale.secondary, 'opacity-70')}>{secondary}</span>
                    )}
                </span>
            )}
        </span>
    )
}
